import { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { motion } from '@/components/public/PublicMotion';
import { fadeInUp, staggerContainer, scaleIn } from '@/hooks/use-scroll-animation';
import { Heading } from '@/components/ui/heading';
import { Button } from '@/components/ui/button';
import { PublicDemoLink } from '@/components/public/PublicDemoLink';
import { Check, ArrowRight, Sparkles, Users, Zap } from 'lucide-react';

// --- Data ---

type BillingCycle = 'monthly' | 'yearly';

interface Plan {
  id: string;
  clients: string;
  clientsLabel: string;
  monthlyPrice: number;
  yearlyPrice: number;
  yearlyTotal: number;
  savings?: number;
  perClientMonthly: string;
  perClientYearly: string;
  isPopular: boolean;
  features: string[];
  cta: string;
}

const plans: Plan[] = [
  {
    id: 'starter',
    clients: '5',
    clientsLabel: '5 clienti attivi',
    monthlyPrice: 49,
    yearlyPrice: 45.75,
    yearlyTotal: 549,
    savings: 49,
    perClientMonthly: '3,3€',
    perClientYearly: '3,1€',
    isPopular: false,
    features: [
      'Tutte le funzionalità incluse',
      'Diario guidato IA per ogni cliente',
      'Dashboard sintesi pre-seduta',
      'Andamenti emotivi nel tempo',
      'Supporto email',
    ],
    cta: 'Inizia con 5 clienti',
  },
  {
    id: 'pro',
    clients: '10',
    clientsLabel: '10 clienti attivi',
    monthlyPrice: 89,
    yearlyPrice: 74.92,
    yearlyTotal: 899,
    savings: 169,
    perClientMonthly: '3€',
    perClientYearly: '3€',
    isPopular: true,
    features: [
      'Tutte le funzionalità incluse',
      'Diario guidato IA per ogni cliente',
      'Dashboard sintesi pre-seduta',
      'Andamenti emotivi nel tempo',
      'Supporto email prioritario',
      'Onboarding dedicato',
    ],
    cta: 'Scegli il piano Pro',
  },
  {
    id: 'growth',
    clients: '20',
    clientsLabel: '20 clienti attivi',
    monthlyPrice: 169,
    yearlyPrice: 129.08,
    yearlyTotal: 1549,
    savings: 479,
    perClientMonthly: '2,8€',
    perClientYearly: '2,2€',
    isPopular: false,
    features: [
      'Tutte le funzionalità incluse',
      'Diario guidato IA per ogni cliente',
      'Dashboard sintesi pre-seduta',
      'Andamenti emotivi nel tempo',
      'Supporto prioritario dedicato',
      'Onboarding dedicato',
    ],
    cta: 'Scala con 20 clienti',
  },
];

const faqs = [
  {
    q: "Posso cambiare piano in corso d'opera?",
    a: 'Sì. Puoi aggiornare o ridurre il tuo piano in qualsiasi momento. Le modifiche diventano effettive nel ciclo di fatturazione successivo.',
  },
  {
    q: 'Cosa succede se ho più clienti di quelli previsti dal piano?',
    a: 'Puoi aggiungere licenze extra al tuo piano esistente. Con il piano mensile ogni licenza aggiuntiva costa 8€/mese, con il piano annuale 6€/mese.',
  },
  {
    q: 'Il cliente deve pagare qualcosa?',
    a: "No. Emovia è uno strumento per psicologi. Il costo è interamente a carico del professionista. L'app per i clienti è gratuita.",
  },
  {
    q: 'Posso provare prima di impegnarmi?',
    a: 'Certamente. Richiedi una demo e ti mostriamo come funziona con i tuoi casi clinici reali, senza nessun impegno.',
  },
];

const pricingJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': 'https://www.emovia.com/#software',
    name: 'Emovia',
    applicationCategory: 'WebApplication',
    operatingSystem: 'Web',
    inLanguage: 'it-IT',
    description:
      'Diario guidato con IA Interrogativa per psicologi e psicoterapeuti: raccoglie ciò che la persona vive tra le sedute e lo restituisce come materiale clinico ordinato.',
    url: 'https://www.emovia.com',
    publisher: { '@id': 'https://www.emovia.com/#organization' },
    offers: plans.map((plan) => ({
      '@type': 'Offer',
      name: `Emovia — ${plan.clientsLabel}`,
      price: plan.monthlyPrice,
      priceCurrency: 'EUR',
      url: 'https://www.emovia.com/prezzi',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: plan.monthlyPrice,
        priceCurrency: 'EUR',
        billingDuration: 1,
        unitCode: 'MON',
      },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a },
    })),
  },
];

// --- Sub-components ---

function BillingToggle({
  cycle,
  onChange,
}: {
  cycle: BillingCycle;
  onChange: (c: BillingCycle) => void;
}) {
  return (
    <div
      className="inline-flex items-center gap-1 rounded-full border border-white/25 bg-white/10 p-1"
      role="group"
      aria-label="Ciclo di fatturazione"
    >
      <button
        type="button"
        onClick={() => onChange('monthly')}
        className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
          cycle === 'monthly'
            ? 'bg-white text-primary shadow-sm'
            : 'text-white/80 hover:text-white'
        }`}
        aria-pressed={cycle === 'monthly'}
      >
        Mensile
      </button>
      <button
        type="button"
        onClick={() => onChange('yearly')}
        className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
          cycle === 'yearly'
            ? 'bg-white text-primary shadow-sm'
            : 'text-white/80 hover:text-white'
        }`}
        aria-pressed={cycle === 'yearly'}
      >
        Annuale
        <span className="ml-2 rounded-full bg-green-accent px-2 py-0.5 text-xs font-semibold text-green-dark">
          Risparmia
        </span>
      </button>
    </div>
  );
}

function PlanCard({ plan, cycle }: { plan: Plan; cycle: BillingCycle }) {
  const perClient = cycle === 'monthly' ? plan.perClientMonthly : plan.perClientYearly;
  const displayPrice = cycle === 'yearly' ? `${plan.yearlyTotal}€` : `${plan.monthlyPrice}€`;
  const displayPeriod = cycle === 'yearly' ? '/anno' : '/mese';

  return (
    <motion.div
      variants={scaleIn}
      whileHover={plan.isPopular
        ? { scale: 1.02, transition: { duration: 0.2, ease: [0.25, 1, 0.5, 1] } }
        : { y: -4, transition: { duration: 0.2, ease: [0.25, 1, 0.5, 1] } }
      }
      className={`relative pt-3.5 ${plan.isPopular ? 'z-10' : ''}`}
    >
      {plan.isPopular ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="absolute top-0 left-1/2 -translate-x-1/2 z-10"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow">
            <Sparkles aria-hidden className="h-3 w-3" />
            Più popolare
          </span>
        </motion.div>
      ) : null}

      <div className={`flex h-full flex-col overflow-hidden rounded-2xl bg-card transition-shadow duration-300 hover:shadow-md ${
        plan.isPopular
          ? 'border-2 border-primary shadow-[0_0_0_4px_rgba(0,51,204,0.1),0_4px_6px_-1px_rgba(0,0,0,0.1)]'
          : 'border border-border shadow-sm hover:border-primary/20'
      }`}>

      <div className={`px-6 pt-8 pb-6 ${plan.isPopular ? 'bg-blue-lighter' : ''}`}>
        <div className="flex items-center gap-2 mb-1">
          <Users aria-hidden className={`h-4 w-4 ${plan.isPopular ? 'text-primary' : 'text-primary-dark'}`} />
          <span className="text-sm font-medium text-primary-dark">{plan.clientsLabel}</span>
        </div>

        <div className="mt-4 flex items-baseline gap-1">
          <AnimatePresence mode="wait">
            <motion.span
              key={displayPrice}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: [0.25, 1, 0.5, 1] }}
              className="text-4xl font-bold text-primary"
            >
              {displayPrice}
            </motion.span>
          </AnimatePresence>
          <AnimatePresence mode="wait">
            <motion.span
              key={displayPeriod}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="text-sm text-primary-dark"
            >
              {displayPeriod}
            </motion.span>
          </AnimatePresence>
        </div>

        <AnimatePresence mode="wait">
          {cycle === 'yearly' && plan.savings ? (
            <motion.p
              key="savings"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="mt-1 text-sm font-medium text-green-dark"
            >
              Risparmi {plan.savings}€ rispetto al mensile
            </motion.p>
          ) : null}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.p
            key={`per-client-${cycle}`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="mt-1 text-sm text-primary-dark"
          >
            Circa {perClient} per cliente/mese*
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="flex flex-1 flex-col px-6 pb-8">
        <ul className="mb-8 space-y-3 flex-1">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <Check
                aria-hidden
                className={`mt-0.5 h-4 w-4 shrink-0 ${plan.isPopular ? 'text-primary' : 'text-primary-dark'}`}
              />
              <span className="text-sm text-primary-dark">{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          asChild
          variant={plan.isPopular ? 'default' : 'outline'}
          className="w-full"
          size="lg"
        >
          <PublicDemoLink location={`pricing_card_${plan.id}`}>
            {plan.cta}
            <ArrowRight aria-hidden className="ml-2 h-4 w-4" />
          </PublicDemoLink>
        </Button>
      </div>
      </div>
    </motion.div>
  );
}

function EnterpriseCard({ cycle }: { cycle: BillingCycle }) {
  const addonPrice = cycle === 'monthly' ? 8 : 6;

  return (
    <motion.div
      variants={fadeInUp}
      whileHover={{ y: -2, transition: { duration: 0.2, ease: [0.25, 1, 0.5, 1] } }}
      className="flex flex-col gap-6 rounded-2xl border border-dashed border-border bg-blue-lighter p-8 transition-shadow duration-300 hover:shadow-sm md:flex-row md:items-center md:justify-between"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-accent">
          <Zap aria-hidden className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="font-semibold text-primary">Più di 20 clienti attivi?</p>
          <p className="mt-1 text-sm text-primary-dark">
            Aggiungi clienti al tuo piano a{' '}
            <AnimatePresence mode="wait">
              <motion.strong
                key={addonPrice}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="text-primary"
              >
                {addonPrice}€/mese per licenza
              </motion.strong>
            </AnimatePresence>{' '}
            {cycle === 'yearly' ? '(piano annuale)' : '(piano mensile)'}. Scala senza limiti.
          </p>
        </div>
      </div>
      <Button asChild variant="outline" className="shrink-0">
        <PublicDemoLink location="pricing_enterprise">
          Parla con noi
          <ArrowRight aria-hidden className="ml-2 h-4 w-4" />
        </PublicDemoLink>
      </Button>
    </motion.div>
  );
}

// --- Page ---

export default function Prezzi() {
  const [cycle, setCycle] = useState<BillingCycle>('monthly');

  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | Prezzi e Piani"
        description="Scopri i piani Emovia per psicologi e psicoterapeuti e scegli gli strumenti adatti al tuo lavoro clinico tra le sedute."
        path="/prezzi"
        jsonLd={pricingJsonLd}
      />

      {/* Hero */}
      <section className="bg-primary py-20 md:py-28">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="mx-auto max-w-2xl text-center"
          >
            <motion.span
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
              className="mb-4 inline-block rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-sm font-medium text-white/90"
            >
              Prezzi trasparenti, senza sorprese
            </motion.span>

            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading
                level={1}
                font="display"
                align="center"
                className="text-white"
              >
                Paga solo per i clienti che segui
              </Heading>
            </motion.div>

            <motion.p
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="mt-6 text-lg text-white/85 leading-relaxed"
            >
              Nessun costo nascosto. Nessun vincolo a lungo termine. Puoi cambiare, aggiungere o
              rimuovere licenze in qualsiasi momento durante il mese.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="mt-10 flex justify-center"
            >
              <BillingToggle cycle={cycle} onChange={setCycle} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="py-20 md:py-28">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={staggerContainer}
            className="grid gap-6 md:grid-cols-3 items-stretch"
          >
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} cycle={cycle} />
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8"
          >
            <EnterpriseCard cycle={cycle} />
          </motion.div>
        </div>
      </section>

      {/* Note / licenze */}
      <section className="bg-muted/30 py-12">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-2xl space-y-4 text-sm text-primary-dark"
          >
            <p>
              <strong className="text-primary">* Come funzionano le licenze:</strong> 1 licenza = 1
              cliente attivo. Le licenze vengono assegnate direttamente dallo psicologo e possono essere
              cambiate durante il mese in base alle esigenze cliniche.
            </p>
            <p>
              <strong className="text-primary">** Costo per cliente:</strong> Calcolato ipotizzando 3
              sedute al mese per cliente attivo. Il valore reale dipende dalla frequenza delle sedute.
            </p>
            <p>
              Il piano annuale viene fatturato in un&apos;unica soluzione all&apos;inizio del periodo. Il piano
              mensile si rinnova automaticamente ogni mese. Puoi disdire in qualsiasi momento.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ essenziale */}
      <section className="py-20 md:py-28">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="mx-auto max-w-3xl"
          >
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading level={2} font="display" className="mb-10 text-primary">
                Domande frequenti
              </Heading>
            </motion.div>

            {faqs.map((faq) => (
              <motion.div
                key={faq.q}
                variants={fadeInUp}
                className="border-b border-border py-6 last:border-0"
              >
                <p className="font-semibold text-primary mb-2">{faq.q}</p>
                <p className="text-primary-dark leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-blue-lighter py-20 md:py-28">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="mx-auto max-w-2xl text-center"
          >
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading level={2} font="display" align="center" className="text-primary">
                Pronto a portare continuità nelle tue sedute?
              </Heading>
            </motion.div>
            <motion.p
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="mt-6 text-lg text-primary-dark leading-relaxed"
            >
              Inizia con una demo gratuita. Ti mostriamo Emovia in azione con un caso reale, senza
              impegno e senza carte di credito.
            </motion.p>
            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="mt-10"
            >
              <Button size="lg" asChild className="text-base px-8 h-12">
                <PublicDemoLink location="prezzi_final_cta">
                  Richiedi una demo gratuita
                  <ArrowRight aria-hidden className="ml-2 h-5 w-5" />
                </PublicDemoLink>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
}
